import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { format, isSameDay } from 'date-fns';
import { useAppContext } from 'src/app/state';
import { IoMdClose } from 'react-icons/io';
import { AppRoutes } from 'src/types/app-routes.enum';
import Button from 'src/app/components/kit/button/Button';
import Text from 'src/app/components/kit/text/Text';
import styles from './CalendarPage.module.css';
import Segmented from 'antd/lib/segmented';
import ConfigProvider from 'antd/lib/config-provider';
import Calendar from 'src/app/components/kit/calendar/Calendar';
import { PiPencilSimpleBold } from 'react-icons/pi';

type CalendarMode = 'Month' | 'Year';

const CalendarPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const dateInParams = params.get('day');
  const date = useMemo(
    () => (dateInParams ? new Date(dateInParams) : new Date()),
    [dateInParams]
  );
  const {
    state: { migraineAttacks },
  } = useAppContext();
  const today = useMemo(() => new Date(), []);
  const yearRange = useMemo(
    () => [
      today.getFullYear() - 1,
      today.getFullYear(),
      today.getFullYear() + 1,
    ],
    [today]
  );
  const [calendarView, setCalendarView] = useState<CalendarMode>('Month');
  const [selectedDate, setSelectedDate] = useState(date ?? today);
  const initialDays = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => new Date(today.getFullYear(), i, 1)),
    [today]
  );
  const migraineDays: Date[] = useMemo(
    () =>
      migraineAttacks
        .filter((record) => record.isMigraineDay)
        .map((record) => record.date!),
    [migraineAttacks]
  );
  const vyduraEveryOtherDayDates: Date[] = useMemo(
    () =>
      migraineAttacks
        .filter((record) => record.vyduraEveryOtherDay === true)
        .map((record) => record.date!),
    [migraineAttacks]
  );
  const vyduraPerAttackDays: Date[] = useMemo(
    () =>
      migraineAttacks
        .filter((record) => record.vyduraPerAttack === true)
        .map((record) => record.date!),
    [migraineAttacks]
  );
  const isOngoingAttack = useMemo(
    () => migraineDays.some((day) => isSameDay(day!, selectedDate)),
    [migraineDays, selectedDate]
  );
  const isVyduraIntakeDay = useMemo(
    () => vyduraEveryOtherDayDates.some((day) => isSameDay(day!, selectedDate)),
    [vyduraEveryOtherDayDates, selectedDate]
  );
  const openMigraineRecordPage = () => {
    const params = new URLSearchParams();
    params.set('day', selectedDate.toDateString());
    params.set('location', 'calendar');
    navigate(`${AppRoutes.MigraineAttack}?${params.toString()}`);
  };
  const openVyduraIntakePage = () => {
    const params = new URLSearchParams();
    params.set('day', selectedDate.toDateString());
    params.set('location', 'calendar');
    navigate(`${AppRoutes.VyduraIntake}?${params.toString()}`);
  };
  const handleYearCalendarSelection = (date: Date) => {
    if (date.getFullYear() === today.getFullYear()) {
      setSelectedDate(date);
      setCalendarView('Month');
    }
  };
  const handleWindowClose = () => navigate(AppRoutes.Home);
  const scrollToSelectedDate = useCallback(() => {
    if (calendarView === 'Month') {
      const element = document.querySelector(
        `#${format(selectedDate, 'MMMM')}`
      );
      element?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest',
      });
    } else {
      const element = document.querySelector(
        `#year_${selectedDate.getFullYear()}`
      );
      element?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest',
      });
    }
  }, [calendarView, selectedDate]);
  const onReturnToInitialState = () => {
    setSelectedDate(today);
    scrollToSelectedDate();
  };

  useEffect(scrollToSelectedDate, [scrollToSelectedDate]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div className={styles['header-top']}>
          <Button
            type='transparent'
            width='content-width'
            style={{ padding: 0, height: '21.6px', margin: 'auto 0' }}
            onClick={handleWindowClose}
          >
            <IoMdClose size={20} />
          </Button>
          <ConfigProvider
            theme={{
              components: {
                Segmented: {
                  itemSelectedBg: 'var(--color-dark-grey)',
                  itemSelectedColor: 'white',
                  trackBg: 'var(--color-grey)',
                  controlPaddingHorizontal: 18,
                  trackPadding: 0,
                  itemActiveBg: 'var(--color-grey)',
                  itemHoverBg: 'var(--color-grey)',
                },
              },
            }}
          >
            <Segmented
              value={calendarView}
              shape='round'
              options={['Month', 'Year']}
              onChange={(value) => setCalendarView(value as CalendarMode)}
            />
          </ConfigProvider>
          <Text
            tag='span'
            weight='semi-bold'
            style={{ textAlign: 'right', lineHeight: '32px' }}
            onClick={onReturnToInitialState}
          >
            Today
          </Text>
        </div>
        {calendarView === 'Month' && (
          <div className='grid grid-cols-7 text-center text-text-gray mt-[20px]'>
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
              <div key={day} className='text-[13px]'>
                {day}
              </div>
            ))}
          </div>
        )}
      </div>
      {calendarView === 'Month' ? (
        <>
          <div className={styles.content}>
            {initialDays.map((day, index) => (
              <Calendar
                key={day.getTime()}
                id={format(day, 'MMMM')}
                type='light'
                initialDay={day}
                selectedDate={selectedDate}
                showCurrentDate={false}
                onDateSelect={setSelectedDate}
                vyduraEveryDayDates={vyduraEveryOtherDayDates}
                vyduraPerAttacksDates={vyduraPerAttackDays}
                migraineDates={migraineDays}
                maxDate={today}
                className={index === 11 ? '' : 'border-b border-b-grey'}
              />
            ))}
          </div>
          <div className={styles.footer}>
            <Button
              type={isOngoingAttack ? 'white' : 'primary'}
              width='content-width'
              onClick={openMigraineRecordPage}
              style={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {isOngoingAttack ? (
                <>
                  <PiPencilSimpleBold />
                  &nbsp;&nbsp;
                  {`Edit${isSameDay(selectedDate, new Date()) ? ' ongoing ' : ' '}attack`}
                </>
              ) : (
                '+ Record attack'
              )}
            </Button>
            {!isOngoingAttack && isVyduraIntakeDay && (
              <Button
                type='transparent'
                width='content-width'
                onClick={openVyduraIntakePage}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  fontWeight: 500,
                }}
              >
                <PiPencilSimpleBold />
                &nbsp;&nbsp;Edit Vydura Intake
              </Button>
            )}
          </div>
        </>
      ) : (
        <div className={styles['content-year']}>
          {yearRange.map((year, index) => (
            <div
              key={year}
              id={`year_${year}`}
              className={`w-full p-[16px] ${index === 2 ? '' : 'border-b border-b-grey'}`}
            >
              <div className='text-center text-[18px] my-[10px] flex items-center justify-center'>
                <span
                  className={year === today.getFullYear() ? 'font-bold' : ''}
                >
                  {year}
                </span>
              </div>
              <div className='grid grid-cols-3'>
                {Array.from({ length: 12 }, (_, i) => new Date(year, i, 1)).map(
                  (day) => (
                    <Calendar
                      key={day.getTime()}
                      type='light'
                      initialDay={day}
                      maxDate={today}
                      selectedDate={selectedDate}
                      showCurrentDate={false}
                      onDateSelect={handleYearCalendarSelection}
                      vyduraEveryDayDates={vyduraEveryOtherDayDates}
                      vyduraPerAttacksDates={vyduraPerAttackDays}
                      migraineDates={migraineDays}
                      size='small'
                    />
                  )
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CalendarPage;
